import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Breadcrumb from "../components/Breadcrumb";
import ContactButton from "../components/ContactButton";

function ArticleTechnologie() {
  return (
    <>
      <Helmet>
        <title>Comment choisir la bonne technologie pour votre site web en 2025 | Guillaume Colin</title>
        <meta name="description" content="Guide complet pour choisir la technologie adaptée à votre projet web : React, Vue.js, WordPress, Node.js. Comparaison des avantages et inconvénients." />
        <meta name="keywords" content="choisir technologie site web, React vs Vue.js, WordPress vs React, Node.js, développement web 2025" />
      </Helmet>
      
      <div className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Fil d'Ariane */}
          <Breadcrumb
            items={[
              { label: "Accueil", path: "/" },
              { label: "Blog", path: "/blog" },
              { label: "Comment choisir la bonne technologie pour votre site web en 2025" }
            ]}
          />
          
          {/* Header de l'article */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center text-sm text-slate-400 mb-4">
              <span>26 Octobre 2025</span>
              <span className="mx-2">•</span>
              <span>8 min de lecture</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400">
              Comment choisir la bonne technologie pour votre site web en 2025
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed">
              React, Vue.js, WordPress, Node.js... Le choix de la technologie peut faire ou défaire votre projet web. 
              Découvrez comment faire le bon choix selon vos besoins, budget et objectifs.
            </p>
          </motion.div>

          {/* Contenu de l'article */}
          <motion.article 
            className="prose prose-lg prose-invert max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-orange-400">Pourquoi le choix de la technologie est crucial</h2>
            
            <p className="text-slate-300 mb-6 leading-relaxed">
              Choisir la mauvaise technologie peut coûter cher : développement plus long, maintenance difficile, 
              performances décevantes, et même abandon du projet. En 2025, avec l'évolution rapide du web, 
              il est essentiel de faire le bon choix dès le départ.
            </p>

            <h2 className="text-3xl font-bold mb-6 text-orange-400">Les critères de choix essentiels</h2>
            
            <div className="bg-slate-800 p-6 rounded-xl mb-8 border border-slate-700">
              <h3 className="text-xl font-semibold mb-4 text-white">1. Type de projet</h3>
              <ul className="text-slate-300 space-y-2">
                <li><strong>Site vitrine :</strong> WordPress, React statique, ou Vue.js</li>
                <li><strong>E-commerce :</strong> Shopify, WooCommerce, ou React + Node.js</li>
                <li><strong>Application web :</strong> React, Vue.js, ou Angular</li>
                <li><strong>Application mobile :</strong> React Native ou Flutter</li>
              </ul>
            </div>

            <div className="bg-slate-800 p-6 rounded-xl mb-8 border border-slate-700">
              <h3 className="text-xl font-semibold mb-4 text-white">2. Budget et délais</h3>
              <ul className="text-slate-300 space-y-2">
                <li><strong>Budget serré :</strong> WordPress ou solutions no-code</li>
                <li><strong>Délais courts :</strong> Templates React ou Vue.js</li>
                <li><strong>Budget confortable :</strong> Développement sur mesure</li>
                <li><strong>Projet long terme :</strong> Technologies modernes et maintenables</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-orange-400">Comparaison des technologies populaires</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                <h3 className="text-xl font-semibold mb-4 text-blue-400">React</h3>
                <div className="space-y-3 text-slate-300">
                  <p><strong>✅ Avantages :</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Écosystème énorme</li>
                    <li>• Performance excellente</li>
                    <li>• Communauté active</li>
                    <li>• Flexibilité maximale</li>
                  </ul>
                  <p><strong>❌ Inconvénients :</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Courbe d'apprentissage</li>
                    <li>• Développement plus long</li>
                    <li>• Coût plus élevé</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                <h3 className="text-xl font-semibold mb-4 text-green-400">Vue.js</h3>
                <div className="space-y-3 text-slate-300">
                  <p><strong>✅ Avantages :</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Facile à apprendre</li>
                    <li>• Documentation excellente</li>
                    <li>• Performance optimale</li>
                    <li>• Syntaxe intuitive</li>
                  </ul>
                  <p><strong>❌ Inconvénients :</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Écosystème plus petit</li>
                    <li>• Moins d'emplois</li>
                    <li>• Communauté plus réduite</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-orange-400">Mon conseil d'expert</h2>
            
            <div className="bg-gradient-to-r from-blue-600/20 to-orange-600/20 p-6 rounded-xl mb-8 border border-blue-500/30">
              <p className="text-slate-300 leading-relaxed">
                <strong>Pour 80% des projets, je recommande React + Node.js.</strong> Cette combinaison offre 
                le meilleur rapport performance/flexibilité/maintenabilité. React pour l'interface utilisateur 
                moderne et réactive, Node.js pour l'API robuste et scalable.
              </p>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-orange-400">Questions à vous poser</h2>
            
            <div className="bg-slate-800 p-6 rounded-xl mb-8 border border-slate-700">
              <ol className="text-slate-300 space-y-3">
                <li><strong>1.</strong> Quel est votre budget total pour le projet ?</li>
                <li><strong>2.</strong> Dans combien de temps voulez-vous être en ligne ?</li>
                <li><strong>3.</strong> Avez-vous besoin de fonctionnalités complexes ?</li>
                <li><strong>4.</strong> Voulez-vous pouvoir modifier le contenu vous-même ?</li>
                <li><strong>5.</strong> Prévoyez-vous de faire évoluer le site ?</li>
              </ol>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-orange-400">Conclusion</h2>
            
            <p className="text-slate-300 mb-8 leading-relaxed">
              Le choix de la technologie n'est pas anodin. Il impacte directement le coût, les délais, 
              et la réussite de votre projet. Prenez le temps de bien définir vos besoins avant de choisir, 
              et n'hésitez pas à demander conseil à un professionnel.
            </p>

            {/* CTA */}
            <div className="bg-slate-800 p-8 rounded-xl border border-slate-700 text-center">
              <h3 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-blue-400">
                Besoin d'aide pour choisir ?
              </h3>
              <p className="text-slate-300 mb-6">
                Je vous aide à choisir la technologie adaptée à votre projet lors d'une consultation gratuite.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <ContactButton variant="default">📧 Consultation gratuite</ContactButton>
                <motion.a
                  href="https://wa.me/33666742480?text=Bonjour, j'ai lu votre article sur le choix de technologie et j'aimerais discuter de mon projet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  💬 WhatsApp
                </motion.a>
              </div>
            </div>
          </motion.article>

          {/* Navigation */}
          <motion.div 
            className="mt-12 pt-8 border-t border-slate-700"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex justify-between items-center">
              <a 
                href="/blog"
                className="inline-flex items-center text-orange-400 hover:text-orange-300 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour au blog
              </a>
              <a 
                href="/services"
                className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-200"
              >
                Mes services
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default ArticleTechnologie;
